import { PieChart } from '@mui/x-charts/PieChart'
import { Box, CircularProgress, Grid, Paper, Typography } from '@mui/material'
import { useGetSubSegmentAnalysisQuery } from 'store/dashboard'
import ChartTitle from './ChartTitle'
import NoDataAvailable from './NoDataAvailable'
import { useTimeRange } from './TimeRangeContext'

const SEGMENT_COLORS = [
  '#E91E63',
  '#F0A500',
  '#7B68EE',
  '#20B2AA',
  '#FF6B6B',
  '#4A90D9',
  '#FFD93D',
  '#6BCB77',
  '#4D96FF',
  '#002C5F'
]

const SubSegmentAnalysisChart = () => {
  const { startDate, endDate } = useTimeRange()
  const { data, isLoading, isFetching, isError, refetch } = useGetSubSegmentAnalysisQuery({
    startDate,
    endDate
  })

  const categories = data?.categories ?? []

  const renderContent = () => {
    if (isLoading || isFetching) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220 }}>
          <CircularProgress />
        </Box>
      )
    }

    if (isError) {
      return <NoDataAvailable variant='error' onRefresh={refetch} />
    }
    if (!data) {
      return <NoDataAvailable onRefresh={refetch} />
    }

    if (categories.length === 0) {
      return <NoDataAvailable onRefresh={refetch} />
    }

    return (
      <Grid container spacing={2} alignItems='center'>
        <Grid item xs={12} sm={4}>
          <PieChart
            series={[
              {
                data: categories.map((c, i) => ({
                  id: i,
                  value: c.count,
                  label: c.name,
                  color: SEGMENT_COLORS[i % SEGMENT_COLORS.length]
                })),
                innerRadius: 70,
                outerRadius: 100,
                cx: 110,
                highlightScope: { faded: 'global', highlighted: 'item' }
              }
            ]}
            height={220}
            slotProps={{ legend: { hidden: true } }}
          />
        </Grid>

        <Grid item xs={12} sm={8}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
            {categories.map((c, i) => {
              const color = SEGMENT_COLORS[i % SEGMENT_COLORS.length]
              return (
                <Box key={c.name}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '2px',
                        bgcolor: color,
                        flexShrink: 0
                      }}
                    />
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                      {c.name}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 700, color, fontSize: 18, ml: '20px' }}>
                    {c.percentage}%
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Grid>
      </Grid>
    )
  }

  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: 'none'
      }}
      elevation={0}
    >
      <ChartTitle tooltip='Displays voucher claim distribution by category and acquisition source'>
        Sub Segment Analysis
      </ChartTitle>
      {renderContent()}
    </Paper>
  )
}

export default SubSegmentAnalysisChart
